import classNames from 'classnames'
import Icon, { IconProps } from './Icon'

type BulletButtonVariant = 'solid' | 'light' | 'outlined'
type BulletButtonIcon = IconProps & {
  direction?: 'leading' | 'end'
}
type BulletButtonIconProps = {
  icon: BulletButtonIcon
  hasSibling: boolean
}
export type BulletButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    icon?: BulletButtonIcon
    loading?: boolean
    variant?: BulletButtonVariant
  }

function getBulletButtonStyle(
  disabled: boolean,
  variant: BulletButtonVariant,
): string {
  if (disabled) {
    if (variant === 'outlined') {
      return 'text-text500 babu-text-button-bold border border-text500 bg-transparent shadow-none'
    }
    return 'text-text500 babu-text-button-bold bg-bg200 shadow-none'
  }
  if (variant === 'solid') {
    return 'text-white babu-text-button-bold bg-primary900 shadow-elevation-primary-E400 focus:text-primary300 focus:bg-primary1200 focus:shadow-elevation-primary-E100 active:text-primary300 active:bg-primary1200 active:shadow-elevation-primary-E100'
  }
  if (variant === 'outlined') {
    return 'text-primary900 babu-text-button-bold border border-primary900 bg-transparent focus:text-primary1200 focus:border-primary1200 active:text-primary1200 active:border-primary1200'
  }
  return 'text-text babu-text-button-bold bg-white shadow-elevation-gray-E400 focus:text-text500 focus:shadow-elevation-gray-E100 active:shadow-elevation-gray-E100'
}

function BulletButtonIcon({ icon, hasSibling }: BulletButtonIconProps) {
  return (
    <Icon
      {...icon}
      style={icon.style}
      className={classNames(
        { 'mr-2': hasSibling && icon.direction === 'leading' },
        { 'ml-2': hasSibling && icon.direction === 'end' },
      )}
    />
  )
}

export default function BulletButton({
  children,
  className,
  icon,
  disabled = false,
  loading = false,
  variant = 'light',
  ...nativeButtonProps
}: BulletButtonProps) {
  const buttonDisabled = disabled || loading
  const iconDirection = icon?.direction ? icon.direction : 'leading'

  return (
    <button
      {...nativeButtonProps}
      disabled={buttonDisabled}
      className={classNames(
        'flex justify-center items-center w-full py-3 font-semibold rounded-2xl',
        getBulletButtonStyle(buttonDisabled, variant),
        className,
      )}>
      {loading ? (
        <Icon name="loading" />
      ) : icon ? (
        iconDirection === 'leading' ? (
          <>
            <BulletButtonIcon icon={icon} hasSibling={!!children} />
            {children}
          </>
        ) : (
          <>
            {children}
            <BulletButtonIcon icon={icon} hasSibling={!!children} />
          </>
        )
      ) : (
        children
      )}
    </button>
  )
}
